import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { createFeedbackModalStyles } from "./create-feedback-modal.css";

import "../button/wow-button";
import "../form-elements/textarea/textarea";
import "../modal/wow-modal";

import "@phosphor-icons/webcomponents/PhThumbsDown";
import "@phosphor-icons/webcomponents/PhThumbsUp";
import { NotificationService } from "../notification";
import { SignalElement, inject } from "ui-common/di";
import type { IAuthState } from "ui-common/services/auth/types";
import type { IRpcService } from "ui-common/services/rpc/types";

@customElement("create-feedback-modal")
export class CreateFeedbackModal extends SignalElement {
  static override styles = [createFeedbackModalStyles];

  @inject<IAuthState>("authState")
  private authState!: IAuthState;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  @property({ type: String })
  variant: "thumbs" | "label" = "thumbs";

  @property({ type: String }) headerText = "We'd love your feedback 💬";
  @property({ type: String }) sentimentMessage = "How did you experience?";
  @property({ type: String }) sentimentPrompt = "What could we improve to make this easier for you?";
  @property({ type: String }) labelPrompt = "Share your thoughts with us";
  @property({ type: String }) labelPlaceholder = "Type your feedback here...";
  @property({ type: String }) labelError = "Oops, feedback can't be empty.";
  @property({ type: String }) successMessage = "Thanks for sharing! You're helping us make WOW even better.";
  @property({ type: String }) footerButtonText = "Send feedback";
  @property({ type: String }) footerButtonLoadingText = "Sending feedback...";

  @state()
  private status: "idle" | "sending" | "success" = "idle";

  @state()
  private feedbackSentiment?: boolean;

  @state()
  private feedbackComment: string = "";

  private handleLikeClick() {
    this.feedbackSentiment = true;
    this.submitFeedback();
  }

  private handleDislikeClick() {
    this.feedbackSentiment = false;
    this.variant = "label";
  }

  private async submitFeedback() {
    if (this.feedbackSentiment === undefined && !this.feedbackComment) return;
    try {
      this.status = "sending";
      const pageUrl = window.location.href;
      const currentUser = this.authState.currentUser.get();
      const username = currentUser?.name;
      const userId = currentUser?.id;
      const feedbackData = {
        pageUrl,
        ...(this.feedbackComment && { comment: this.feedbackComment }),
        ...(this.feedbackSentiment !== undefined && { isPositive: this.feedbackSentiment }),
        ...(username && { username }),
        ...(userId && { userId }),
      };

      await this.rpcService.call("createFeedback", feedbackData);
    } catch (error) {
      NotificationService.error(`Error sending feedback: ${error}`);
    } finally {
      this.status = "success";
    }
  }

  /**
   * Close the modal - simplified to always close
   */
  private closeModal = () => {
    if (this.feedbackSentiment !== undefined && this.status === "idle") {
      this.submitFeedback();
    }
    this.dispatchEvent(new CustomEvent("modal-close", { bubbles: true, composed: true }));
  };

  private handleSaveFeedback = async () => {
    await this.submitFeedback();
  };

  private renderContent() {
    if (this.status === "success") {
      return html`<div class="subtitle">${this.successMessage}</div>`;
    }

    if (this.variant === "thumbs") {
      return html`
        <div class="subtitle">${this.sentimentMessage}</div>
        <div class="thumbs-wrapper">
          <wow-button
            variant="neutral"
            size="large"
            .color=${"var(--sds-color-white-800)"}
            .backgroundColor=${"var(--sds-color-background-positive-default)"}
            rounded
            square
            @click=${this.handleLikeClick}
          >
            <ph-thumbs-up size="24"></ph-thumbs-up>
          </wow-button>
          <wow-button
            variant="neutral"
            size="large"
            .color=${"var(--sds-color-white-800)"}
            .backgroundColor=${"var(--sds-color-background-danger-default)"}
            rounded
            square
            @click=${this.handleDislikeClick}
          >
            <ph-thumbs-down size="24"></ph-thumbs-down>
          </wow-button>
        </div>
      `;
    }

    return html`
      <wow-textarea
        name="feedbackComment"
        .label=${this.feedbackSentiment === undefined ? this.labelPrompt : this.sentimentPrompt}
        .placeholder=${this.labelPlaceholder}
        maxlength="500"
        required
        @input=${(e: Event) => (this.feedbackComment = (e.target as HTMLInputElement).value)}
      ></wow-textarea>
    `;
  }

  protected override render() {
    return html`
      <wow-modal
        variant="responsive"
        @close=${this.closeModal}
        @footer-button-clicked=${this.handleSaveFeedback}
        ?hide-primary-button=${this.variant === "thumbs" || this.status === "success"}
        ?disable-primary-button=${this.status === "sending" ||
        (this.feedbackSentiment === undefined && !this.feedbackComment)}
      >
        <span slot="header">${this.headerText}</span>
        <span slot="footer-button-text">
          ${this.status === "sending" ? this.footerButtonLoadingText : this.footerButtonText}
        </span>

        <div class="modal-content">${this.renderContent()}</div>
      </wow-modal>
    `;
  }
}
